import React, { Component } from 'react'
import { connect } from 'react-redux'
import { reject, insert } from 'ramda'
import { Table, Button } from '@driftt/tide-core-legacy'
import { captureException } from 'utils/SentryHelpers'
import IconSVG from 'svg-inline-react'

import websiteCtaSrc from '!svg-inline!images/svg/website-cta.svg'
import { PrioritiesColumns } from './constants'
import { NonSpecialRowFormatter } from './NonSpecialRowFormatter'
import { OutboundRowFormatter } from './OutboundRowFormatter'
import { ABMRowFormatter } from './ABMRowFormatter'
import { fetchPrioritizedPlaybooks } from '../../api/playbooks'

import * as UserSelectors from 'app/modules/user/selectors'

import './style.styl'

const EMAIL_PLAYBOOK_TYPES = [
  'SEND_AN_AUTOMATED_EMAIL',
  'SEND_A_ONETIME_EMAIL',
  'SEND_A_ONETIME_OUTBOUND_EMAIL',
  'OUTBOUND_SEQUENCE',
  'MARKETING_SEQUENCE',
  'MARKETING_ONE_TIME_SEQUENCE',
  'BLOG_NURTURE',
  'NEW_BLOG_CONTENT',
  'NEWSLETTER',
  'PRODUCT_ANNOUNCEMENT',
]

const REPORT_TO_DATA_TYPE = {
  ABM: 'abm',
  TALK_TO_YOUR_OUTBOUND_PROSPECTS: 'outbound',
}

const DATA_FIELD_TO_PLAYBOOK_LIST_CLASS = {
  abm: '.playbooks-abm',
  outbound: '.playbooks-outbound',
  nonSpecial: '.playbooks-normal',
}

class PrioritiesTable extends Component {
  constructor(props) {
    super(props)

    this.state = {
      idPriorityList: [],
      playbooks: null,
      totalABMPlaybooks: null,
      totalOutboundPlaybooks: null,
      totalNonSpecialPlaybooks: null,
      prioritizeRequestPending: false,
      parentTableClass: '',
      changedRow: null,
    }
  }

  componentDidMount() {
    this.getPlaybooks()
  }

  bucketPlaybooksByType(playbooks) {
    const outboundPlaybooks = playbooks.filter(pb => pb.reportType === 'TALK_TO_YOUR_OUTBOUND_PROSPECTS')
    const abmPlaybooks = playbooks.filter(pb => pb.reportType === 'ABM')
    const remainingPlaybooks = playbooks.filter(
      pb => !EMAIL_PLAYBOOK_TYPES.concat(['ABM', 'TALK_TO_YOUR_OUTBOUND_PROSPECTS']).includes(pb.reportType)
    )

    const playBookTypes = {
      abm: abmPlaybooks,
      outbound: outboundPlaybooks,
      nonSpecial: remainingPlaybooks,
    }

    this.setState({
      idPriorityList: outboundPlaybooks.concat(abmPlaybooks, remainingPlaybooks).map(pb => pb.id),
      totalABMPlaybooks: playBookTypes.abm.length,
      totalOutboundPlaybooks: playBookTypes.outbound.length,
      totalNonSpecialPlaybooks: playBookTypes.nonSpecial.length,
      prioritizeRequestPending: false,
      playbooks: playBookTypes,
    })
  }

  postProcessData() {
    const { rawData } = this.state
    this.bucketPlaybooksByType(rawData)
  }

  getPlaybooks = async () => {
    this.setState({ prioritizeRequestPending: true })

    try {
      const data = await fetchPrioritizedPlaybooks()
      this.setState({ rawData: data }, this.postProcessData)
    } catch(err) {
      captureException(err)
      this.setState({ playbooks: null })
    }
  }

  changePriority = (option, playbook) => {
    const { idPriorityList } = this.state
    const { storeData } = this.props
    const newValue = option.value

    const isId = id => id === playbook.id
    const newIndex = newValue - 1

    const updatedPriorityList = insert(newIndex, playbook.id, reject(isId, idPriorityList))

    storeData(updatedPriorityList, false)

    this.setState(
      {
        idPriorityList: updatedPriorityList,
      },
      this.renderHackyColorFlash(playbook.reportType, newIndex)
    )
  }

  renderHackyColorFlash(reportType, newIndex) {
    // TODO REMOVE THIS WHEN WE CAN ADD CUSTOM CLASSES TO TIDE TABLE ROWS
    const playbookReportField = REPORT_TO_DATA_TYPE[reportType] || 'nonSpecial'
    this.setState({
      parentTableClass: DATA_FIELD_TO_PLAYBOOK_LIST_CLASS[playbookReportField],
      parentTableDataType: playbookReportField,
      changedRow: newIndex,
    })
  }

  renderEmptyState = () => (
    <div className="playbook-list-table-empty-state">
      <IconSVG src={websiteCtaSrc} className="playbook-list-table-empty-state-icon" />
      <p>Let&#39;s get started. Create your first playbook.</p>
      <Button type="primary" href="/marketplace">
        New Playbook
      </Button>
    </div>
  )

  renderRows(rowFormatter, playbooks, totalPlaybooks) {
    const { idPriorityList } = this.state
    const { orgUsers } = this.props
    return idPriorityList
      .map((playbookId, i) => {
        const playbook = playbooks.find(pb => pb.id === playbookId)
        if (!playbook) return
        playbook.priority = i + 1
        return { data: rowFormatter(playbook, totalPlaybooks, this.changePriority.bind(this), orgUsers) }
      })
      .filter(Boolean)
  }

  render() {
    const {
      playbooks,
      totalNonSpecialPlaybooks,
      totalOutboundPlaybooks,
      totalABMPlaybooks,
      prioritizeRequestPending,
      idPriorityList,
      changedRow,
      parentTableClass,
      parentTableDataType,
    } = this.state

    const sizes = {
      totalABMPlaybooks,
      totalOutboundPlaybooks,
      totalNonSpecialPlaybooks,
    }

    const playbooksListIsEmpty = !!playbooks && idPriorityList.length === 0

    const styledRowOffset = row => {
      if (parentTableDataType === 'nonSpecial') {
        return row - (totalABMPlaybooks + totalOutboundPlaybooks) + 1
      }
      return row
    }

    // TODO REMOVE THIS WHEN WE CAN ADD CUSTOM CLASSES TO TIDE TABLE ROWS
    const styleString = `${parentTableClass} tbody tr:nth-child(${styledRowOffset(
      changedRow
    )}) td { animation: glowChange 500ms ease-in-out 500ms normal 3 running; }`

    return (
      <div className="playbook-list-table-container">
        <style> {styleString}</style>
        {prioritizeRequestPending && <div className="playbook-list-table-loader" />}
        {playbooksListIsEmpty && this.renderEmptyState()}
        {!!playbooks && (
          <div>
            <Table
              data={this.renderRows(OutboundRowFormatter, playbooks.outbound, sizes)}
              columns={PrioritiesColumns()}
              className="playbook-priority-list-table playbooks-outbound"
            />
            <Table
              data={this.renderRows(ABMRowFormatter, playbooks.abm, sizes)}
              columns={PrioritiesColumns()}
              className="playbook-priority-list-table playbooks-abm"
            />
            <Table
              data={this.renderRows(NonSpecialRowFormatter, playbooks.nonSpecial, sizes)}
              columns={PrioritiesColumns()}
              className="playbook-priority-list-table playbooks-normal"
            />
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state, props) => ({
  orgUsers: UserSelectors.orgUsersById(state, props),
})

export default connect(mapStateToProps, null)(PrioritiesTable)



// WEBPACK FOOTER //
// ./src/scripts/components/PlaybookList/Prioritize/PlaybookPrioritiesTable/index.js